<app-loader [isBusy]="isBusy">
    <ng-template>
        <form [formGroup]="form">
            <div class="modal-header">
                <h4 class="modal-title">{{ 'Create Service Request' | translate }}</h4>
                <button class="close" type="button" aria-label="Close" (click)="activeModal.close(null)">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <div class="modal-body">
                <div class="row">
                    <div class="col-12">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <form-dropdown
                                    labelText="{{ 'Manipulation' | translate }}"
                                    formControlName="manipulationId"
                                    [automaticPopulateWithFirstValue]="true"
                                    [observableInput]="manipulations$"></form-dropdown>
                            </div>
                            @if (isBulkManifest) {
                                <div class="col-md-6 mb-2 d-flex align-items-end">
                                    <form-checkbox
                                        labelText="{{ 'Cargo Unit Specification Included' | translate }}"
                                        contextual="{{
                                            'Check this option if you need to plan and track this non-containerized cargo at an individual unit level (e.g., specific serial numbers, steel coils, plates) instead of bulk summaries.'
                                                | translate
                                        }}"
                                        formControlName="cargoUnitSpecification"></form-checkbox>
                                </div>
                            }
                            @if (
                                form.get('manipulationId').value !== null &&
                                !['VSIN', 'VS-BLT'].includes(form.get('manipulationId').value)
                            ) {
                                <div class="col-md-6 mb-2 d-flex align-items-end">
                                    <form-checkbox
                                        labelText="{{ 'Unspecified MOT Quantity' | translate }}"
                                        kendoTooltip
                                        title="{{
                                            'Bypasses MOT (Mean Of Transport) data entry for this Service Request entirely. ' +
                                                'Transport identification (e.g., license plates or wagon numbers) will instead be recorded during terminal operations ' +
                                                '(e.g., via Storage Documents). To be used when exact number of expected trucks/wagons is unknown.'
                                                | translate
                                        }}"
                                        formControlName="motNbr"></form-checkbox>
                                </div>
                            }
                        </div>
                        <div class="row">
                            <div class="col-2 col-lg-1 pr-0 mb-2">
                                <button
                                    class="btn btn-sm btn-primary btn-sm mt-2"
                                    kendoTooltip
                                    title="{{
                                        (mrnSelect ? 'Change to manual MRN' : 'Change to existing MRN') | translate
                                    }}"
                                    (click)="toggleMRNSelect($event)">
                                    @if (mrnSelect) {
                                        <fa-icon [icon]="['fas', 'keyboard']"></fa-icon>
                                    }
                                    @if (!mrnSelect) {
                                        <fa-icon [icon]="['fas', 'list']"></fa-icon>
                                    }
                                </button>
                            </div>

                            @if (mrnSelect) {
                                <div class="col-md-5 mb-2">
                                    <form-dropdown
                                        labelText="{{ 'Existing MRN' | translate }}"
                                        formControlName="mrnId"
                                        [observableInput]="mrn$"
                                        [automaticPopulateWithFirstValue]="true"
                                        [selectLabel]="selectLabel"></form-dropdown>
                                </div>
                            }

                            @if (!mrnSelect) {
                                <div class="col-md-5 mb-2">
                                    <form-dropdown
                                        labelText="{{ 'MRN Type' | translate }}"
                                        formControlName="customsDeclarationCodeId"
                                        codeListName="CustomsDeclarationCode"></form-dropdown>
                                </div>
                            }
                            @if (!mrnSelect) {
                                <div class="col-md-5 mb-2">
                                    <form-textbox
                                        labelText="{{ 'Manual MRN' | translate }}"
                                        formControlName="mrnString"></form-textbox>
                                </div>
                            }

                            @if (!mrnSelect) {
                                <div class="col-2 col-lg-1 pr-0 mb-2">
                                    <button
                                        class="btn btn-sm btn-primary btn-sm mt-2"
                                        kendoTooltip
                                        title="{{ 'Search' | translate }}"
                                        [disabled]="!form.controls.mrnString.value"
                                        (click)="searchMRN($event)">
                                        <fa-icon [icon]="['fas', 'search']"></fa-icon>
                                    </button>
                                </div>
                            }

                            <div class="col-md-6 mb-2">
                                <form-dropdown
                                    labelText="{{ 'MRN Item' | translate }}"
                                    formControlName="mrnItemId"
                                    [observableInput]="mrnItem$"
                                    [automaticPopulateWithFirstValue]="true"
                                    [selectLabel]="selectLabel"></form-dropdown>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <form-textbox labelText="{{ 'LRN' | translate }}" formControlName="lrn"></form-textbox>
                            </div>
                            @if (
                                form.get('manipulationId').value !== null &&
                                ['VSIN', 'VS-BLT'].includes(form.get('manipulationId').value)
                            ) {
                                <div class="col-md-6 mb-2">
                                    <form-dropdown
                                        labelText="{{ 'Customs Warehouse' | translate }}"
                                        formControlName="customsWarehouseId"
                                        [observableInput]="customsWarehouses$"></form-dropdown>
                                </div>
                            }
                        </div>
                        @if (showVsVsLoadingFields) {
                            <div class="row">
                                <div class="col-md-4 mb-2">
                                    <form-dropdown
                                        labelText="{{ 'Loading Vessel Voyage' | translate }}"
                                        formControlName="loadingVesselVoyage"
                                        displayValue="name"
                                        [observableInput]="loadingVesselVisits$"></form-dropdown>
                                </div>
                                <div class="col-md-4 mb-2">
                                    <form-dropdown
                                        labelText="{{ 'Loading Vessel' | translate }}"
                                        formControlName="loadingVesselId"
                                        displayValue="name"
                                        [observableInput]="loadingVessels$"
                                        [automaticPopulateWithFirstValue]="true"></form-dropdown>
                                </div>
                                <div class="col-md-4 mb-2">
                                    <form-textbox
                                        labelText="{{ 'Loading Voyage Number' | translate }}"
                                        formControlName="loadingVoyageNumber"></form-textbox>
                                </div>
                            </div>
                        }
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <form-datetime
                                    labelText="{{ 'Required Start Date' | translate }}"
                                    formControlName="requiredStartDate"></form-datetime>
                            </div>
                            <div class="col-md-6 mb-2">
                                <form-textbox
                                    labelText="{{ 'Contract Nbr.' | translate }}"
                                    formControlName="contractNbr"></form-textbox>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <form-dropdown
                                    labelText="{{ 'Secondary Terminal' | translate }}"
                                    formControlName="secondaryTerminalId"
                                    contextual="{{
                                        'Select another terminal where cargo for this manipulation is or will be stored.'
                                            | translate
                                    }}"
                                    [observableInput]="secondaryTerminals$"></form-dropdown>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12 mb-2">
                                <form-textbox
                                    formControlName="remark"
                                    labelText="{{ 'Remark' | translate }}"></form-textbox>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <button class="btn btn-sm btn-primary" type="submit" [disabled]="form.invalid" (click)="submit()">
                    {{ 'Create' | translate }}
                </button>
                <button class="btn btn-sm btn-secondary" type="button" (click)="activeModal.close(null)">
                    {{ 'Cancel' | translate }}
                </button>
            </div>
        </form>
    </ng-template>
</app-loader>
